/**
 * Parses a street address into its components: street, house number, letter, and apartment number.
 * Supports formats like "Strømsbuåsen 11 B H0401" or "Strømsbuåsen 11-12B, H0401".
 * @param {string} address - The full address string to parse.
 * @returns {object} - Object containing fullStreetAddress, street, houseNumber, letter, and apartmentNumber.
 */
export const parseStreetAddress = (address) => {
    // Default result object for invalid or incomplete inputs
    const result = {
        fullStreetAddress: address,
        street: '',
        houseNumber: '',
        letter: '',
        apartmentNumber: ''
    };

    if (!address || typeof address !== 'string') {
        return result;
    }

    // Replace commas with spaces and split into parts
    const addressParts = address.replace(/,\s*/g, ' ').trim().split(/\s+/);
    if (addressParts.length < 2) {
        return result; // Need at least street and number
    }

    // Find the part that looks like a house number (e.g., "11", "11-12", "11B")
    const houseNumberIndex = addressParts.findIndex(part =>
        /^\d+(?:-\d+)?[A-Za-z]?$/.test(part)
    );
    if (houseNumberIndex === -1) {
        return result; // No valid house number found
    }

    // Extract the street name (everything before the house number)
    result.street = addressParts.slice(0, houseNumberIndex).join(' ');

    // Split house number part into number and letter (e.g., "11B" -> "11" and "B")
    const housePart = addressParts[houseNumberIndex];
    const [, number, letter = ''] = housePart.match(/(\d+(?:-\d+)?)([A-Za-z]?)/) || [, housePart];

    result.houseNumber = number;
    result.letter = letter;

    // Look at parts after the house number for letter or apartment
    const remainingParts = addressParts.slice(houseNumberIndex + 1);
    if (remainingParts.length > 0) {
        // If no letter yet and first remaining part is a single letter, use it
        if (!result.letter && remainingParts[0]?.length === 1 && /[A-Za-z]/.test(remainingParts[0])) {
            result.letter = remainingParts[0];
            result.apartmentNumber = remainingParts[1] || '';
        } else {
            // Otherwise, first remaining part is the apartment number
            result.apartmentNumber = remainingParts[0];
        }
    }

    return result;
};

export function createAddress(address) {
    return {
        ...address,
        fullStreetAddress: createStreetAddress(address)
    }
}

export function createStreetAddress(
    { street, houseNumber, letter, apartmentNumber }
) {
    return [
        street,
        houseNumber,
        letter,
        apartmentNumber,
    ].filter(Boolean).join(' ')
}
